import {
	describeLineageReason,
	describePublicRetrievalReason,
} from '@/entities/document-lineage';
import { SignUploadedDocumentResponse } from '@/use-cases/sign-uploaded-document';

export type PublicRetrievalOutcome = {
	enabled: boolean;
	// Plain-Spanish explanation when public download was requested but not
	// enabled. Null when the backend sent a reason this build does not know.
	message: string | null;
};

export type LineageOutcome = {
	added: boolean;
	// Plain-Spanish explanation when the page was requested but not added.
	message: string | null;
	verifyUrl: string | null;
	// Null when public download was never requested, or when the backend said
	// nothing about it.
	publicRetrieval: PublicRetrievalOutcome | null;
};

// Turns the sign response into what the "document signed" dialog should say.
// Returns null when there is nothing to show: the page was not requested, or
// the backend said nothing about it.
export function buildLineageOutcome(
	lineagePageRequested: boolean,
	publicRetrievalRequested: boolean,
	response: Pick<
		SignUploadedDocumentResponse,
		| 'lineage_added'
		| 'lineage_reason'
		| 'verify_url'
		| 'public_retrieval'
		| 'public_reason'
	>,
): LineageOutcome | null {
	if (!lineagePageRequested || response.lineage_added === undefined) {
		return null;
	}

	const verifyUrl = response.verify_url ?? null;
	const publicRetrieval = buildPublicRetrievalOutcome(
		publicRetrievalRequested,
		response,
	);

	if (response.lineage_added) {
		return { added: true, message: null, verifyUrl, publicRetrieval };
	}

	return {
		added: false,
		message: describeLineageReason(response.lineage_reason),
		verifyUrl,
		publicRetrieval,
	};
}

// The sentences the "document signed" dialog shows, one per line. Kept as a
// list so the dialog renders each in its own paragraph instead of running them
// together into a single block of text.
export function buildLineageOutcomeSentences(
	outcome: LineageOutcome,
): string[] {
	const sentences = [
		outcome.added
			? 'Se agregó la página de trazabilidad.'
			: (outcome.message ??
				'No se pudo agregar la página de trazabilidad.'),
	];

	if (outcome.publicRetrieval) {
		sentences.push(
			outcome.publicRetrieval.enabled
				? 'La descarga pública está habilitada: quien tenga el QR o el enlace impreso podrá descargar el documento firmado.'
				: (outcome.publicRetrieval.message ??
						'No se pudo habilitar la descarga pública del documento firmado.'),
		);
	}

	return sentences;
}

function buildPublicRetrievalOutcome(
	publicRetrievalRequested: boolean,
	response: Pick<
		SignUploadedDocumentResponse,
		'public_retrieval' | 'public_reason'
	>,
): PublicRetrievalOutcome | null {
	if (!publicRetrievalRequested || response.public_retrieval === undefined) {
		return null;
	}

	if (response.public_retrieval) {
		return { enabled: true, message: null };
	}

	return {
		enabled: false,
		message: describePublicRetrievalReason(response.public_reason),
	};
}
